import React, { useState } from 'react';
import { ChevronDown, ChevronRight, Folder, FolderOpen } from 'lucide-react';
import { allFolderPaths, foldersToDepth, type PathNode } from '../pathTree.ts';

function itemsUnder<T>(node: PathNode<T>): T[] {
  return [...node.items, ...node.folders.flatMap(itemsUnder)];
}

/**
 * A picker list laid out as the folders the files were uploaded in. With `onToggleFolder`, each folder has a
 * checkbox that picks (or unpicks) every selectable file under it.
 */
export function PickerFolderTree<T>({
  node,
  renderItem,
  searching,
  isSelectable,
  isSelected,
  onToggleFolder,
  depth = 0,
  openState,
}: {
  node: PathNode<T>;
  renderItem: (item: T, depth: number) => React.ReactNode;
  searching: boolean;
  isSelectable: (item: T) => boolean;
  isSelected: (item: T) => boolean;
  onToggleFolder?: (items: T[], select: boolean) => void;
  depth?: number;
  /** Shared open/closed state; created by the top-level tree. */
  openState?: [Set<string>, React.Dispatch<React.SetStateAction<Set<string>>>];
}) {
  const own = useState<Set<string>>(() => new Set(foldersToDepth(node, 1)));
  const [open, setOpen] = openState ?? own;
  const openPaths = searching ? new Set(allFolderPaths(node)) : open;

  return (
    <>
      {node.folders.map((folder) => {
        const isOpen = openPaths.has(folder.path);
        const selectable = itemsUnder(folder).filter(isSelectable);
        const picked = selectable.filter(isSelected).length;
        const all = selectable.length > 0 && picked === selectable.length;
        return (
          <li key={`d-${folder.path}`}>
            <div className="flex items-center gap-1.5 bg-gray-50/70 px-3 py-1.5" style={{ paddingLeft: 12 + depth * 18 }}>
              <button
                type="button"
                onClick={() => setOpen((prev) => {
                  const next = new Set(prev);
                  if (next.has(folder.path)) next.delete(folder.path);
                  else next.add(folder.path);
                  return next;
                })}
                aria-expanded={isOpen}
                aria-label={`${isOpen ? 'Collapse' : 'Expand'} folder ${folder.name}`}
                className="rounded p-0.5 text-gray-500 hover:bg-gray-200"
              >
                {isOpen ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
              </button>
              {onToggleFolder ? (
                <input
                  type="checkbox"
                  aria-label={`Pick every file in ${folder.name}`}
                  checked={all}
                  ref={(el) => { if (el) el.indeterminate = picked > 0 && !all; }}
                  disabled={selectable.length === 0}
                  onChange={() => onToggleFolder(selectable, !all)}
                />
              ) : null}
              {isOpen ? <FolderOpen className="h-4 w-4 text-amber-500" /> : <Folder className="h-4 w-4 text-amber-500" />}
              <span className="truncate text-sm font-medium text-gray-800" title={folder.path}>{folder.name}</span>
              <span className="ml-auto shrink-0 text-xs text-gray-400">{folder.count} file{folder.count !== 1 ? 's' : ''}</span>
            </div>
            {isOpen ? (
              <ul className="divide-y divide-gray-100">
                <PickerFolderTree
                  node={folder}
                  renderItem={renderItem}
                  searching={searching}
                  isSelectable={isSelectable}
                  isSelected={isSelected}
                  onToggleFolder={onToggleFolder}
                  depth={depth + 1}
                  openState={[open, setOpen]}
                />
              </ul>
            ) : null}
          </li>
        );
      })}
      {node.items.map((item) => renderItem(item, depth))}
    </>
  );
}
